import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Pencil, Check, AlertTriangle, Calendar, ListChecks } from "lucide-react";
import { getColumnKey, formatDateFrench } from "@/components/tableau/utils/formatters";

const slug = (s) => String(s).replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase();

export function BulkEditDialog({
  open, onClose, count, columns = [], orders = [],
  getColumnType, getColumnDropdownOptions, getColumnStatusConfig,
  onApply,
}) {
  const [phase, setPhase] = useState("edit");
  const [column, setColumn] = useState("");
  const [value, setValue] = useState("");
  const [customMode, setCustomMode] = useState(false);
  const [custom, setCustom] = useState("");

  useEffect(() => {
    if (open) { setPhase("edit"); setColumn(""); setValue(""); setCustomMode(false); setCustom(""); }
  }, [open]);

  // Only offer editable columns (exclude computed "calcul" columns)
  const editableColumns = useMemo(
    () => columns.filter((c) => !["calcul", "fabric"].includes(getColumnType(c.name))),
    [columns, getColumnType]
  );

  const type = column ? getColumnType(column) : "text";

  // Suggested values for dropdown / status columns
  const optionValues = useMemo(() => {
    if (!column) return [];
    const set = new Set();
    if (type === "dropdown") {
      (getColumnDropdownOptions(column) || []).forEach((v) => { if (String(v).trim()) set.add(String(v).trim()); });
      const key = getColumnKey(column);
      orders.forEach((o) => {
        const ed = o.extracted_data || {};
        const v = ed[key] ?? ed[column] ?? ed[column.toUpperCase()];
        if (v !== undefined && v !== null && String(v).trim() !== "") set.add(String(v).trim());
      });
    } else if (type === "status") {
      const cfg = getColumnStatusConfig(column);
      Object.values(cfg?.labels || {}).forEach((l) => { if (l) set.add(l); });
    }
    return Array.from(set).sort();
  }, [column, type, orders, getColumnDropdownOptions, getColumnStatusConfig]);

  const handleSelectColumn = (name) => {
    setColumn(name);
    setValue("");
    setCustomMode(false);
    setCustom("");
  };

  const effectiveValue = type === "checkbox"
    ? value === true
    : (customMode ? custom.trim() : value);

  const canApply = column && (
    type === "checkbox" ? (value === true || value === false)
      : (type === "date" ? !!value : String(effectiveValue).trim() !== "")
  );

  const displayValue = () => {
    if (type === "checkbox") return value === true ? "Coché (Oui)" : "Décoché (Non)";
    if (type === "date") return value ? formatDateFrench(value) : "";
    return effectiveValue;
  };

  const pillClass = (active) =>
    `inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs transition-colors ${
      active
        ? "border-orange-300 bg-orange-50 text-orange-700 font-medium"
        : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50"
    }`;

  const renderValueEditor = () => {
    if (!column) return null;
    if (type === "checkbox") {
      return (
        <div className="flex gap-1.5" data-testid="bulk-edit-checkbox-options">
          <button type="button" className={pillClass(value === true)} onClick={() => setValue(true)} data-testid="bulk-edit-checkbox-true">
            Coché (Oui){value === true && <Check className="w-3.5 h-3.5" />}
          </button>
          <button type="button" className={pillClass(value === false)} onClick={() => setValue(false)} data-testid="bulk-edit-checkbox-false">
            Décoché (Non){value === false && <Check className="w-3.5 h-3.5" />}
          </button>
        </div>
      );
    }
    if (type === "date") {
      return (
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-zinc-400" />
          <Input
            type="date"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="h-9 text-sm w-44"
            data-testid="bulk-edit-date-input"
          />
        </div>
      );
    }
    if (type === "dropdown" || type === "status") {
      return (
        <div>
          <div className="flex flex-wrap gap-1.5" data-testid="bulk-edit-options">
            {optionValues.map((v) => {
              const active = !customMode && value === v;
              return (
                <button key={v} type="button" onClick={() => { setCustomMode(false); setValue(v); }} className={pillClass(active)} data-testid={`bulk-edit-option-${slug(v)}`}>
                  {v}{active && <Check className="w-3.5 h-3.5" />}
                </button>
              );
            })}
            <button type="button" onClick={() => { setCustomMode(true); setValue(""); }} className={pillClass(customMode)} data-testid="bulk-edit-option-autre">
              Autre…{customMode && <Check className="w-3.5 h-3.5" />}
            </button>
          </div>
          {customMode && (
            <Input autoFocus value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Nouvelle valeur" className="mt-2 h-9 text-sm" data-testid="bulk-edit-custom-input" />
          )}
        </div>
      );
    }
    return (
      <Input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Nouvelle valeur"
        className="h-9 text-sm"
        data-testid="bulk-edit-text-input"
      />
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="bulk-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-orange-500" />
            Édition groupée
          </DialogTitle>
        </DialogHeader>

        {phase === "edit" ? (
          <div className="space-y-4 text-sm">
            <p className="text-zinc-600">
              <strong className="text-zinc-800" data-testid="bulk-edit-count">{count}</strong> ligne{count > 1 ? "s" : ""} sélectionnée{count > 1 ? "s" : ""}.
            </p>

            <div>
              <label className="text-xs font-medium text-zinc-600 mb-1.5 block">Colonne à modifier</label>
              <Select value={column} onValueChange={handleSelectColumn}>
                <SelectTrigger className="h-9 text-sm" data-testid="bulk-edit-column-select">
                  <SelectValue placeholder="Choisir une colonne…" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {editableColumns.map((c) => (
                    <SelectItem key={c.name} value={c.name} data-testid={`bulk-edit-column-${slug(c.name)}`}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {column && (
              <div>
                <label className="text-xs font-medium text-zinc-600 mb-1.5 block">Nouvelle valeur</label>
                {renderValueEditor()}
              </div>
            )}

            <div className="flex justify-end items-center gap-2 pt-3 border-t border-zinc-100">
              <Button variant="outline" size="sm" onClick={onClose} data-testid="bulk-edit-cancel">Annuler</Button>
              <Button
                size="sm"
                disabled={!canApply}
                onClick={() => setPhase("confirm")}
                className="gap-1.5 bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="bulk-edit-next"
              >
                <Pencil className="w-4 h-4" /> Appliquer à {count} ligne{count > 1 ? "s" : ""}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 text-sm">
            <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-amber-800" data-testid="bulk-edit-confirm-text">
                Modifier « <strong>{column}</strong> » = « <strong>{displayValue()}</strong> » sur <strong>{count}</strong> ligne{count > 1 ? "s" : ""} ?
              </p>
            </div>
            <p className="text-xs text-zinc-400">Cette action écrasera les valeurs existantes de cette colonne sur les lignes sélectionnées.</p>

            <div className="flex justify-end items-center gap-2 pt-3 border-t border-zinc-100">
              <Button variant="outline" size="sm" onClick={() => setPhase("edit")} data-testid="bulk-edit-back">Retour</Button>
              <Button
                size="sm"
                onClick={() => onApply(column, effectiveValue)}
                className="gap-1.5 bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="bulk-edit-confirm"
              >
                <Check className="w-4 h-4" /> Confirmer
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
